import ExcelJS from 'exceljs';
import type {
  CreateExhibitionBulkItemResult,
  ExhibitionArtistInputData,
} from '../../../shared/dto/EntityRepository';
import type {
  CreateExhibitionBulkItemReq,
  CreateExhibitionReq,
} from '../../../shared/dto/ReqDtoRepository';
import { parseHttpUrl } from '../../../shared/util/urlValidation';
import {
  EXHIBITION_BULK_TEMPLATE_HEADERS,
  EXHIBITION_BULK_TEMPLATE_SHEET_NAME,
} from './buildExhibitionBulkTemplate';

type ExhibitionColumn = (typeof EXHIBITION_BULK_TEMPLATE_HEADERS)[number];

const HEADER_MAP: Record<string, ExhibitionColumn> = {
  venuecode: 'venueCode',
  venue: 'venueCode',
  공간코드: 'venueCode',
  title: 'title',
  전시명: 'title',
  titleen: 'titleEn',
  영문전시명: 'titleEn',
  startdate: 'startDate',
  시작일: 'startDate',
  enddate: 'endDate',
  종료일: 'endDate',
  officialurl: 'officialUrl',
  공식url: 'officialUrl',
  sourceurl: 'sourceUrl',
  출처url: 'sourceUrl',
  artists: 'artists',
  artist: 'artists',
  작가: 'artists',
};

const REQUIRED_HEADERS: ExhibitionColumn[] = [
  'venueCode',
  'title',
  'startDate',
  'endDate',
  'sourceUrl',
];

const URL_FIELDS = new Set<ExhibitionColumn>(['officialUrl', 'sourceUrl']);
const HTTP_URL_SCHEME_REGEX = /^https?:\/\//i;
const DATE_REGEX = /^(\d{4})-(\d{2})-(\d{2})$/;

export type ExhibitionBulkSheetParseErrorCode =
  | 'NO_SHEET'
  | 'UNKNOWN_HEADER'
  | 'BLANK_HEADER_WITH_DATA'
  | 'DUPLICATE_HEADER'
  | 'MISSING_REQUIRED_HEADER';

export class ExhibitionBulkSheetParseError extends Error {
  constructor(
    public readonly code: ExhibitionBulkSheetParseErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'ExhibitionBulkSheetParseError';
  }
}

export interface ExhibitionBulkSheetParseResult {
  exhibitionList: CreateExhibitionBulkItemReq[];
  invalidResults: CreateExhibitionBulkItemResult[];
  totalCount: number;
}

const normalizeHeader = (raw: string): string =>
  raw.trim().toLowerCase().replace(/[\s_-]+/g, '');

const dateToString = (value: Date): string => [
  value.getUTCFullYear(),
  String(value.getUTCMonth() + 1).padStart(2, '0'),
  String(value.getUTCDate()).padStart(2, '0'),
].join('-');

const cellToString = (
  value: ExcelJS.CellValue,
  preferHyperlink = false,
): string => {
  if (value == null) return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value).trim();
  }
  if (value instanceof Date) return dateToString(value);
  if (typeof value === 'object') {
    if ('hyperlink' in value) {
      const link = value as { text?: string; hyperlink?: string };
      const selected = preferHyperlink
        ? link.hyperlink || link.text
        : link.text || link.hyperlink;
      return (selected ?? '').trim();
    }
    if ('result' in value) {
      return cellToString(value.result as ExcelJS.CellValue, preferHyperlink);
    }
    if ('text' in value && typeof value.text === 'string') {
      return value.text.trim();
    }
    if ('richText' in value && Array.isArray(value.richText)) {
      return value.richText
        .map((part: { text?: string }) => part.text ?? '')
        .join('')
        .trim();
    }
  }
  return '';
};

const hasCellData = (value: ExcelJS.CellValue): boolean => {
  if (value == null) return false;
  if (typeof value === 'object'
    && ('formula' in value || 'sharedFormula' in value || 'error' in value)) {
    return true;
  }
  return cellToString(value, true) !== '';
};

const getCellError = (value: ExcelJS.CellValue): string | null => {
  if (value == null || value instanceof Date || typeof value !== 'object') {
    return null;
  }
  if ('error' in value) return String(value.error);
  if ('result' in value) {
    if (value.result == null) return 'formula has no cached result';
    return getCellError(value.result as ExcelJS.CellValue);
  }
  if ('formula' in value || 'sharedFormula' in value) {
    return 'formula has no cached result';
  }
  return null;
};

const getColumnKeys = (
  sheet: ExcelJS.Worksheet,
): { columnKeys: Array<ExhibitionColumn | null>; colCount: number } => {
  const headerRow = sheet.getRow(1);
  const colCount = Math.max(sheet.columnCount, headerRow.cellCount);
  const columnKeys: Array<ExhibitionColumn | null> = [];
  const mappedHeaders = new Set<ExhibitionColumn>();

  for (let col = 1; col <= colCount; col += 1) {
    const rawHeader = cellToString(headerRow.getCell(col).value);
    if (!rawHeader) {
      let hasData = false;
      sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
        if (rowNumber > 1 && hasCellData(row.getCell(col).value)) hasData = true;
      });
      if (hasData) {
        throw new ExhibitionBulkSheetParseError(
          'BLANK_HEADER_WITH_DATA',
          `${sheet.name} column ${col} contains data but has no header.`,
        );
      }
      columnKeys[col] = null;
      continue;
    }

    const mapped = HEADER_MAP[normalizeHeader(rawHeader)];
    if (!mapped) {
      throw new ExhibitionBulkSheetParseError(
        'UNKNOWN_HEADER',
        `Unknown header "${rawHeader}" in ${sheet.name} column ${col}.`,
      );
    }
    if (mappedHeaders.has(mapped)) {
      throw new ExhibitionBulkSheetParseError(
        'DUPLICATE_HEADER',
        `Header "${rawHeader}" duplicates ${mapped} in ${sheet.name}.`,
      );
    }
    mappedHeaders.add(mapped);
    columnKeys[col] = mapped;
  }

  const missingHeaders = REQUIRED_HEADERS.filter((key) => !mappedHeaders.has(key));
  if (missingHeaders.length > 0) {
    throw new ExhibitionBulkSheetParseError(
      'MISSING_REQUIRED_HEADER',
      `Missing required headers in ${sheet.name}: ${missingHeaders.join(', ')}.`,
    );
  }

  return { columnKeys, colCount };
};

const isValidDate = (value: string): boolean => {
  const matched = value.match(DATE_REGEX);
  if (!matched) return false;
  const year = Number(matched[1]);
  const month = Number(matched[2]);
  const day = Number(matched[3]);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  return day <= daysInMonth;
};

const isValidHttpUrl = (value: string): boolean =>
  HTTP_URL_SCHEME_REGEX.test(value) && parseHttpUrl(value) !== null;

const optionalArtistValue = (
  item: Record<string, unknown>,
  field: 'nameEn' | 'instagramHandle' | 'architraveHandle',
  label: string,
  errors: string[],
): string => {
  const value = item[field];
  if (value == null) return '';
  if (typeof value !== 'string') {
    errors.push(`${label}.${field} must be a string.`);
    return '';
  }
  return value.trim();
};

const parseArtists = (
  raw: string,
): { artists?: ExhibitionArtistInputData[]; error?: string } => {
  if (!raw) return { artists: [] };

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return { error: 'artists must be a valid JSON array.' };
  }
  if (!Array.isArray(parsed)) {
    return { error: 'artists must be a JSON array.' };
  }

  const artists: ExhibitionArtistInputData[] = [];
  const errors: string[] = [];
  parsed.forEach((value, index) => {
    const label = `artists[${index}]`;
    if (value == null || typeof value !== 'object' || Array.isArray(value)) {
      errors.push(`${label} must be an object.`);
      return;
    }
    const item = value as Record<string, unknown>;
    if (typeof item.name !== 'string' || !item.name.trim()) {
      errors.push(`${label}.name is required.`);
      return;
    }

    artists.push({
      name: item.name.trim(),
      nameEn: optionalArtistValue(item, 'nameEn', label, errors),
      instagramHandle: optionalArtistValue(item, 'instagramHandle', label, errors),
      architraveHandle: optionalArtistValue(item, 'architraveHandle', label, errors),
    });
  });

  if (errors.length > 0) return { error: errors.join(' ') };
  return { artists };
};

const invalidResult = (
  rowNumber: number,
  errors: string[],
): CreateExhibitionBulkItemResult => ({
  rowNumber,
  status: 'INVALID',
  exhibitionId: null,
  message: errors.join(' '),
  artistConflicts: [],
});

export const parseExhibitionBulkSheet = async (
  file: File,
): Promise<ExhibitionBulkSheetParseResult> => {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(await file.arrayBuffer());
  const normalizedSheetName = normalizeHeader(EXHIBITION_BULK_TEMPLATE_SHEET_NAME);
  const sheet = workbook.worksheets.find(
    (candidate) => normalizeHeader(candidate.name) === normalizedSheetName,
  ) ?? workbook.worksheets[0];
  if (!sheet) {
    throw new ExhibitionBulkSheetParseError('NO_SHEET', 'The workbook has no sheet.');
  }

  const { columnKeys, colCount } = getColumnKeys(sheet);
  const exhibitionList: CreateExhibitionBulkItemReq[] = [];
  const invalidResults: CreateExhibitionBulkItemResult[] = [];
  let totalCount = 0;

  sheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
    if (rowNumber === 1) return;

    const values: Partial<Record<ExhibitionColumn, string>> = {};
    const errors: string[] = [];
    let hasData = false;

    for (let col = 1; col <= colCount; col += 1) {
      const key = columnKeys[col];
      if (!key) continue;
      const cellValue = row.getCell(col).value;
      if (hasCellData(cellValue)) hasData = true;
      const text = cellToString(cellValue, URL_FIELDS.has(key));
      if (text) values[key] = text;
      const cellError = getCellError(cellValue);
      if (cellError) errors.push(`${key}: ${cellError}.`);
    }

    if (!hasData) return;
    totalCount += 1;

    const missingFields = REQUIRED_HEADERS.filter((key) => !values[key]);
    if (missingFields.length > 0) {
      errors.push(`Missing required fields: ${missingFields.join(', ')}.`);
    }

    if (values.startDate && !isValidDate(values.startDate)) {
      errors.push('startDate must be a valid date in YYYY-MM-DD format.');
    }
    if (values.endDate && !isValidDate(values.endDate)) {
      errors.push('endDate must be a valid date in YYYY-MM-DD format.');
    }
    if (values.startDate && values.endDate
      && isValidDate(values.startDate)
      && isValidDate(values.endDate)
      && values.startDate > values.endDate) {
      errors.push('startDate must be on or before endDate.');
    }

    if (values.sourceUrl && !isValidHttpUrl(values.sourceUrl)) {
      errors.push('sourceUrl must be a valid http/https URL.');
    }
    if (values.officialUrl && !isValidHttpUrl(values.officialUrl)) {
      errors.push('officialUrl must be a valid http/https URL.');
    }

    const parsedArtists = parseArtists(values.artists ?? '');
    if (parsedArtists.error) errors.push(parsedArtists.error);

    if (errors.length > 0) {
      invalidResults.push(invalidResult(rowNumber, errors));
      return;
    }

    const exhibition: CreateExhibitionReq = {
      venueCode: values.venueCode!,
      title: values.title!,
      startDate: values.startDate!,
      endDate: values.endDate!,
      sourceUrl: values.sourceUrl!,
      artists: parsedArtists.artists ?? [],
    };
    if (values.titleEn) exhibition.titleEn = values.titleEn;
    if (values.officialUrl) exhibition.officialUrl = values.officialUrl;
    exhibitionList.push({ rowNumber, exhibition });
  });

  return { exhibitionList, invalidResults, totalCount };
};
